import * as React from 'react';
import { observer } from 'mobx-react';

import { WebGivingUserAction } from '../../webgiving-user-actions';
import { ExternalLink, RightChevronLink, Link, DisabledRightChevronLink } from '../../primitives/link/link';
import { KioskLinkDetails, buildKioskLink } from '../../utils/kiosk-link';
import { isFunction } from '../../../Shared/utils/is-function';
import { Tooltip, TooltipPlacement } from '../../components/tooltip/tooltip';
import { WebGivingPushpayInfo, PaymentLabel } from '../../webgiving-generated';
import { ModalHeader, ModalContents } from '../../components/modal/modal';
import { injectWebGivingProps, InjectableWebGivingProps, raiseAction } from '../../utils/injectable-props';
import { Text } from '../../components/text';

import * as style from './help-modal.less';

export interface IHelpModalProps {
	showResendSecurityCode: boolean;
	pushpayInfo: WebGivingPushpayInfo;
	resendCodeDisabled: boolean;
	kioskLinkDetails: KioskLinkDetails;
	paymentLabel: PaymentLabel;
	isRecurringPayment: boolean;
	showAccountLinks?: boolean;
	showKioskLinks?: boolean;
	onRequestClose?: () => void;
}

export class HelpModalHeader extends React.Component {
	render() {
		return <ModalHeader text="Need help?" />;
	}
}

@injectWebGivingProps
@observer
export class HelpModalContent extends React.Component<IHelpModalProps & InjectableWebGivingProps, {}> {
	public render() {
		const {
			showResendSecurityCode,
			kioskLinkDetails,
			resendCodeDisabled,
			isRecurringPayment,
			paymentLabel,
			showAccountLinks,
			showKioskLinks,
			pushpayInfo: { ManageAccountUrl, HelpCenterLink, UpdateProfileUrl, ResetPasswordUrl },
		} = this.props;

		const recurringUnsupported = isRecurringPayment && (
			<p className={style.details}>
				<Text>Recurring {paymentLabel.NounPluralLowerCase} are not supported when {paymentLabel.VerbPresentTenseLowerCase} as a guest</Text>
			</p>
		);

		const sendCode = showResendSecurityCode ? (
			<section className={style.section}>
				<h2 className={`${style.text} ${style.header}`}><Text>Resend security code</Text></h2>
				<div className={style.link}>
					<div className={style.linkItem}>
						{
							resendCodeDisabled
								? <Tooltip placement={TooltipPlacement.Bottom} content={<Text>We can only send you a code every 20 seconds.</Text>} hideOnMobile>
									<DisabledRightChevronLink branded={true}><Text>Via text message</Text></DisabledRightChevronLink>
								</Tooltip>
								: <ResendTextMessageLink resendTextMessage={this.resendTextMessage} />
						}
					</div>
					<div className={style.linkItem}>
						<RightChevronLink branded={true} acceptanceTestTargetId="Via voice call" onClick={this.resendVoiceCall}>
							<Text>Via voice call</Text>
						</RightChevronLink>
					</div>
				</div>
			</section>
		) : (
				<section className={style.section}>
					<h2 className={`${style.text} ${style.header}`}><Text>Receive a security code by voice call</Text></h2>
					<div className={style.link}>
						<RightChevronLink branded={true} acceptanceTestTargetId="Send code via voice call" onClick={this.sendVoiceCall}>
							<Text>Send code via voice call</Text>
						</RightChevronLink>
					</div>
				</section>
			);

		const countryNotListedSection = showKioskLinks && kioskLinkDetails.supported ? (
			<section className={style.section}>
				<div className={style.text}>
					<h2 className={style.header}><Text>Don’t see your country listed?</Text></h2>
					{recurringUnsupported}
				</div>
				<div className={style.link}>
					<ExternalLink
						branded={true}
						acceptanceTestTargetId="Give as guest"
						href={buildKioskLink('wg-country-not-supported', kioskLinkDetails)}><Text>{paymentLabel.VerbSentenceCase} as guest</Text></ExternalLink>
				</div>
			</section>
		) : null;

		const noAccountNeededSection = showKioskLinks && kioskLinkDetails.supported ? (
			<section className={style.section}>
				<div className={style.text}>
					<h2 className={style.header}><Text>{paymentLabel.VerbSentenceCase} as a guest without creating an account</Text></h2>
					{recurringUnsupported}
				</div>
				<div className={style.link}>
					<ExternalLink branded={true}
						acceptanceTestTargetId="Give as guest"
						href={buildKioskLink('wg-guest', kioskLinkDetails)}>
						<Text>{paymentLabel.VerbSentenceCase} as guest</Text>
					</ExternalLink>
				</div>
			</section>
		) : null;

		return (
			<ModalContents>
				<p className="lead">
					<Text>We understand that sometimes there can be issues with receiving text messages. Please select an option from the list below if you are experiencing any issues.</Text>
				</p>
				{countryNotListedSection}

				{sendCode}
				{noAccountNeededSection}

				{showAccountLinks && <section className={style.section}>
					<div className={style.text}>
						<h2 className={style.header}><Text>{paymentLabel.VerbSentenceCase} with an email &amp; password</Text></h2>
						<p className={style.details}><Text>This option is only available if you have {paymentLabel.VerbPastTenseLowerCase} through Pushpay before.</Text></p>
					</div>
					<div className={style.link}>
						<RightChevronLink branded={true}
							acceptanceTestTargetId="Sign into your account"
							href={ManageAccountUrl}>
								<Text>Sign into your account</Text>
						</RightChevronLink>
					</div>
				</section>}

				<section className={style.section}>
					<div className={style.text}>
						<h2 className={style.header}><Text>Changed your mobile number</Text></h2>
						<p className={style.details}>
							<Text>You will need to sign in to your account using your email and password and then update your mobile number.
							If you don't have a password already you can
							create one by using the</Text> <Link branded={true} acceptanceTestTargetId="Reset password" href={ResetPasswordUrl} openInNewTab>
								<Text>Reset password</Text>
							</Link> <Text>link.</Text>
						</p>
					</div>
					{showAccountLinks && <div className={style.link}>
						<ExternalLink branded={true}
							acceptanceTestTargetId="Update mobile"
							href={UpdateProfileUrl}>
							<Text>Update mobile</Text>
						</ExternalLink>
					</div>}
				</section>

				<section className={style.section}>
					<div className={style.text}>
						<h2 className={style.header}><Text>Still need help?</Text></h2>
					</div>
					<div className={style.link}>
						<ExternalLink branded={true} acceptanceTestTargetId="Visit Help Center" href={HelpCenterLink}>
							<Text>Visit Help Center</Text>
						</ExternalLink>
					</div>
				</section>
			</ModalContents>
		);
	}

	private sendVoiceCall = () => {
		this.requestClose();
		raiseAction(new WebGivingUserAction.RequestConfirmationCode(true), this.props);
	}

	private resendVoiceCall = () => {
		this.requestClose();
		raiseAction(new WebGivingUserAction.RequestResendOfConfirmationCode(true), this.props);
	}

	private resendTextMessage = () => {
		this.requestClose();
		raiseAction(new WebGivingUserAction.RequestResendOfConfirmationCode(false), this.props);
	}

	private requestClose() {
		if (isFunction(this.props.onRequestClose)) {
			this.props.onRequestClose();
		}
	}
}

const ResendTextMessageLink = ({ resendTextMessage, disabled }: { resendTextMessage: () => void, disabled?: boolean }) => {
	return (
		<RightChevronLink branded={true} acceptanceTestTargetId="Via text message" href="" disabled={disabled} onClick={resendTextMessage}>
			<Text>Via text message</Text>
		</RightChevronLink>
	);
};
